import {
    Component,
    forwardRef,
    input,
    InputSignal,
    ViewChild,
    effect,
    CUSTOM_ELEMENTS_SCHEMA,
    signal,
    WritableSignal,
    inject,
    OnDestroy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TeamService } from '../../services/team.service';
import { BusinessServices, ITeam } from '@shs-digi-care-monorepo/interfaces';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of, catchError, tap, finalize, filter } from 'rxjs';
import { CommonModule } from '@angular/common';

/**
 * Team picker component with search functionality.
 * Exposes team ID as value and provides editable dropdown with team search.
 */
@Component({
    selector: 'sh-team-picker',
    templateUrl: './sh-team-picker.component.html',
    standalone: true,
    imports: [CommonModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShTeamPickerComponent),
            multi: true
        }
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShTeamPickerComponent implements ControlValueAccessor, OnDestroy {
    @ViewChild('dropdown', { static: false }) public dropdownElement: unknown;

    private readonly teamService = inject(TeamService);

    // Inputs
    public label: InputSignal<string> = input<string>('Team');
    public disabled: InputSignal<boolean> = input<boolean>(false);
    public mandatory: InputSignal<boolean> = input<boolean>(false);
    public error: InputSignal<boolean> = input<boolean>(false);
    public placeholder: InputSignal<string> = input<string>('Search for team...');
    public businessService: InputSignal<BusinessServices | undefined> = input<BusinessServices | undefined>(undefined);

    // Internal state
    protected currentValue: string | null = null; // Team ID
    protected selectedTeam: ITeam | null = null;
    protected searchResults: WritableSignal<ITeam[]> = signal([]);
    protected isLoading: WritableSignal<boolean> = signal(false);
    protected isLoadingValue: WritableSignal<boolean> = signal(false);
    protected isDisabled: WritableSignal<boolean> = signal(false);
    protected searchTerm: WritableSignal<string> = signal('');

    private searchSubject = new Subject<string>();

    private onChange: (value: string | null) => void = () => {};

    private onTouched: () => void = () => {};

    constructor() {
        // Set up search with debounce
        this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((_term) => {
                    this.isLoading.set(true);
                    return this.teamService.getTeams(0, 100, this.businessService()).pipe(
                        catchError((error) => {
                            console.error('Error searching teams:', error);
                            return of({ items: [], total: 0, page: 0, pageSize: 0 });
                        }),
                        finalize(() => this.isLoading.set(false))
                    );
                }),
                tap((result) => {
                    const teams = result.items || [];
                    const searchLower = this.searchTerm().toLowerCase();
                    // Filter teams by name locally
                    const filtered = searchLower
                        ? teams.filter((team) => team.name.toLowerCase().includes(searchLower))
                        : teams;
                    this.searchResults.set(filtered);
                })
            )
            .subscribe();

        // Load selected team when value changes
        effect(() => {
            if (this.currentValue && !this.selectedTeam) {
                this.loadTeamById(this.currentValue);
            }
        });

        // Load initial page of teams. Use an effect so this runs (and re-runs)
        // once the `businessService` input signal has been bound by Angular –
        // otherwise the very first load would happen with `undefined` and
        // ignore the business-service filter.
        effect(() => {
            // Read input so the effect re-executes when it changes.
            this.businessService();
            this.loadInitialPage();
        });
    }

    // ControlValueAccessor implementation
    public writeValue(value: string | null): void {
        this.currentValue = value;
        if (value) {
            this.loadTeamById(value);
        } else {
            this.selectedTeam = null;
        }
    }

    public registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    /**
     * Load the first page of teams without a search term.
     */
    private loadInitialPage(): void {
        this.isLoading.set(true);
        this.teamService
            .getTeams(0, 100, this.businessService())
            .pipe(
                tap((result) => {
                    this.searchResults.set(result.items || []);
                }),
                catchError((error) => {
                    console.error('Error loading initial teams:', error);
                    return of({ items: [], total: 0, page: 0, pageSize: 0 });
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    /**
     * Handle search input change
     */
    protected handleSearchInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const term = input.value;
        this.searchTerm.set(term);
        this.searchSubject.next(term);
    }

    /**
     * Handle team selection from dropdown
     */
    protected handleTeamSelect(team: ITeam): void {
        this.currentValue = team.id;
        this.selectedTeam = team;
        this.searchTerm.set('');
        this.searchResults.set([]);
        this.onChange(team.id);
        this.onTouched();
    }

    /**
     * Handle clear selection
     */
    protected handleClear(): void {
        this.currentValue = null;
        this.selectedTeam = null;
        this.searchTerm.set('');
        this.searchResults.set([]);
        this.onChange(null);
        this.onTouched();
    }

    /**
     * Load team details by ID
     */
    private loadTeamById(id: string): void {
        this.isLoadingValue.set(true);
        this.teamService
            .getTeamById(id)
            .pipe(
                filter((team): team is ITeam => !!team),
                tap((team) => {
                    this.selectedTeam = team;
                }),
                catchError((error) => {
                    console.error('Error loading team:', error);
                    this.selectedTeam = null;
                    return of(null);
                }),
                finalize(() => this.isLoadingValue.set(false))
            )
            .subscribe();
    }

    /**
     * Get display name for a team
     */
    protected getTeamDisplayName(team: ITeam | null): string {
        if (!team) return '';
        return team.name || team.id;
    }

    /**
     * Check if a team is currently selected
     */
    protected isTeamActive(teamId: string): boolean {
        return this.currentValue === teamId;
    }

    public ngOnDestroy(): void {
        this.searchSubject.complete();
    }
}
